import { alertsDrawerData, clearSignals, isAlertsDrawerOpen } from 'Pages/ReportsAlerts/Alerts/Conditions/@signals'
import { alertsSection } from 'Pages/ReportsAlerts/Alerts/@signals'

// ─── Modal ranges ─────────────────────────────────────────────────────────────
// Each range corresponds to one condition-type drawer.
// isOpen checks both that the drawer is visible AND the correct form is rendered.
export const MODAL_RANGES = [
	{
		from: 8, to: 18,
		open: () => openDrawer('Price Comparison'),
		close: clearSignals,
		isOpen: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-pc-name"]'),
	},
	{
		from: 19, to: 24,
		open: () => openDrawer('Not Available'),
		close: clearSignals,
		isOpen: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-na-name"]'),
	},
	{
		from: 25, to: 32,
		open: () => openDrawer('Price Change'),
		close: clearSignals,
		isOpen: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-pch-name"]'),
	},
	{
		from: 33, to: 44,
		open: () => openDrawer('Min & Max Price'),
		close: clearSignals,
		isOpen: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-mm-name"]'),
	},
	{
		from: 45, to: 49,
		open: () => openDrawer('Rating'),
		close: clearSignals,
		isOpen: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-rt-name"]'),
	},
]

// ─── DOM helpers ────────────────────────────────────────────────────────────

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root, .MuiTab-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findTab(text) {
	const tabs = document.querySelectorAll('.MuiTab-root')
	for (const t of tabs) {
		if (t.textContent.trim().toLowerCase().includes(text.toLowerCase())) return t
	}
	return null
}

function findTableColumn(headerText) {
	const headers = document.querySelectorAll('th, [class*="MUIDataTableHeadCell"]')
	for (const h of headers) {
		if (h.textContent.trim().toLowerCase().includes(headerText.toLowerCase())) {
			return h
		}
	}
	return null
}

// ─── Language detection ──────────────────────────────────────────────────────

const ES = navigator.language?.startsWith('es')

// Close drawer on tour skip/close during drawer section
const drawerCleanup = () => clearSignals()

// Open a specific condition drawer (switches tab + opens drawer)
function openDrawer(type) {
	alertsSection.value = type
	alertsDrawerData.value = { type }
}

// ─── Steps ───────────────────────────────────────────────────────────────────

export const TOUR_STEPS = [
	// ─── 0: Intro ───────────────────────────────────────────────────────────────
	{
		type: 'intro',
		icon: 'bell',
		title: ES ? 'Bienvenido a Condiciones de Alerta' : 'Welcome to Alert Conditions',
		description: ES
			? 'Las Condiciones de Alerta definen las reglas que activan notificaciones cuando los datos de precios cumplen criterios especificos. Configura condiciones para monitorizar comparaciones de precios, disponibilidad, cambios de precio, umbrales minimos/maximos y valoraciones en tus informes.'
			: 'Alert Conditions define the rules that trigger notifications when pricing data meets specific criteria. Set up conditions to monitor price comparisons, availability, price changes, min/max thresholds, and ratings across your reports.',
	},
	// ─── 1: Alert Type Tabs ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const tabs = document.querySelector('.MuiTabs-root')
			return tabs || findTab('Price Comparison')?.closest('.MuiTabs-root')
		},
		icon: 'layer-group',
		title: ES ? 'Pestanas de Tipo de Alerta' : 'Alert Type Tabs',
		description: ES
			? 'Cinco tipos de condiciones de alerta disponibles. Cambia entre pestanas para ver y gestionar las condiciones de cada tipo. Cada pestana carga su propio conjunto de condiciones de forma independiente.'
			: 'Five types of alert conditions available. Switch between tabs to view and manage conditions for each type. Each tab loads its own set of conditions independently.',
		example: ES
			? 'Comparacion de Precios | No Disponible | Cambio de Precio | Min y Max | Valoracion'
			: 'Price Comparison | Not Available | Price Change | Min & Max | Rating',
	},
	// ─── 2: Price Comparison tab ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTab('Price Comparison'),
		icon: 'hand-holding-dollar',
		title: ES ? 'Comparacion de Precios' : 'Price Comparison',
		description: ES
			? 'Compara precios entre tu sitio/proveedor principal y la competencia. Configura condiciones como "alertar cuando la competencia es un 10% mas barata" o "alertar cuando la diferencia de precio supere los 20$".'
			: 'Compare prices between your main site/provider and competitors. Set conditions like "alert when competitor is 10% cheaper" or "alert when price difference exceeds $20".',
		example: ES
			? 'Mas Barato | Mas Caro | Diferencia de Precio — en % o $'
			: 'Cheaper | More Expensive | Price Difference — in % or $',
	},
	// ─── 3: Not Available tab ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTab('Not Available'),
		icon: 'circle-xmark',
		title: ES ? 'No Disponible' : 'Not Available',
		description: ES
			? 'Recibe alertas cuando una categoria de coche o proveedor deja de estar disponible en sitios especificos. Util para detectar problemas de inventario o falta de stock en la competencia.'
			: 'Get alerted when a car category or provider becomes unavailable on specific sites. Useful for detecting inventory issues or competitor stock-outs.',
	},
	// ─── 4: Price Change tab ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTab('Price Change'),
		icon: 'money-bill-1-wave',
		title: ES ? 'Cambio de Precio' : 'Price Change',
		description: ES
			? 'Monitoriza fluctuaciones de precios a lo largo del tiempo. Establece umbrales para ser notificado cuando un precio sube o baja un porcentaje o cantidad determinada en comparacion con ejecuciones anteriores.'
			: 'Monitor price fluctuations over time. Set thresholds to be notified when a price increases or decreases by a certain percentage or amount compared to previous executions.',
	},
	// ─── 5: Min & Max Price tab ──────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTab('Min & Max Price'),
		icon: 'plus-minus',
		title: ES ? 'Precio Minimo y Maximo' : 'Min & Max Price',
		description: ES
			? 'Define limites de precio. Recibe alertas cuando los precios superen un umbral maximo o caigan por debajo de un minimo — util para detectar errores de precios o movimientos extremos del mercado.'
			: 'Define price boundaries. Get alerted when prices go above a maximum threshold or below a minimum — useful for detecting pricing errors or extreme market shifts.',
		example: ES
			? 'Mas Caro | Mas Barato | Superior a | Inferior a'
			: 'Most Expensive | Cheapest | Higher Than | Lower Than',
	},
	// ─── 6: Rating tab ───────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const tabs = document.querySelectorAll('.MuiTab-root')
			for (const t of tabs) {
				if (t.textContent.trim().toLowerCase() === 'rating') return t
			}
			return null
		},
		icon: 'star-half-stroke',
		title: ES ? 'Valoracion' : 'Rating',
		description: ES
			? 'Rastrea cambios en las valoraciones de clientes en proveedores y plataformas. Configura condiciones para alertar cuando las valoraciones caen por debajo de un umbral o cuando las valoraciones de la competencia cambian significativamente.'
			: 'Track customer rating changes across providers and platforms. Set conditions to alert when ratings drop below a threshold or when competitors\' ratings change significantly.',
	},
	// ─── 7: New Condition button → opens Price Comparison drawer ────────────────
	{
		type: 'field',
		findTarget: () => {
			const btn = findByText('New condition')
			return btn?.closest('button') || btn?.parentElement
		},
		icon: 'cowbell-circle-plus',
		title: ES ? 'Crear Nueva Condicion' : 'Create New Condition',
		description: ES
			? 'Haz clic para abrir el formulario de creacion. Recorreremos los cinco tipos de condicion uno a uno, campo por campo. Empezamos por Comparacion de Precios — el tipo mas completo.'
			: 'Click to open the creation form. We will walk through all five condition types one by one, field by field. We start with Price Comparison — the most complete type.',
		action: () => openDrawer('Price Comparison'),
		waitFor: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-pc-name"]'),
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// DRAWER 1 — Price Comparison
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 8: Condition Name ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-name"]'),
		icon: 'pen-line',
		title: ES ? 'Nombre de la Condicion' : 'Condition Name',
		description: ES
			? 'Un nombre descriptivo para identificar esta condicion en la tabla. Usa nombres que expliquen que se esta monitorizando, por ejemplo "EuroDrive mas barato que HorizonCars en Demo City". Entre 5 y 100 caracteres.'
			: 'A descriptive name to identify this condition in the table. Use names that explain what is being monitored, e.g. "EuroDrive cheaper than HorizonCars in Demo City". Between 5 and 100 characters.',
		reverseAction: () => clearSignals(),
		cleanup: drawerCleanup,
	},
	// ─── 9: Main Site ────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-main-site"]'),
		icon: 'globe',
		title: ES ? 'Sitio Principal' : 'Main Site',
		description: ES
			? 'El sitio web de referencia — TU sitio o el sitio que quieres defender. Los precios del sitio principal se compararan contra los sitios objetivo. Solo se puede seleccionar un sitio principal.'
			: 'The reference website — YOUR site or the site you want to defend. Main site prices will be compared against target sites. Only one main site can be selected.',
		cleanup: drawerCleanup,
	},
	// ─── 10: Main Providers ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-main-providers"]'),
		icon: 'building',
		title: ES ? 'Proveedores Principales' : 'Main Providers',
		description: ES
			? 'Los proveedores (companias de alquiler) de tu sitio principal que se usaran como referencia de precio. Puedes filtrar por tier (marca paraguas) o seleccionar proveedores individuales.'
			: 'The providers (rental companies) from your main site used as the price reference. You can filter by tier (umbrella brand) or select individual providers.',
		cleanup: drawerCleanup,
	},
	// ─── 11: Comparator ──────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-comparator"]'),
		icon: 'scale-balanced',
		title: ES ? 'Tipo de Comparacion' : 'Comparison Type',
		description: ES
			? 'Define la relacion de precio que activa la alerta: "Mas barato" (competencia mas cara que yo), "Mas caro" (competencia mas barata que yo) o "Diferencia de precio" (diferencia absoluta entre precios). Cada opcion muestra un icono diferente.'
			: 'Defines the price relationship that triggers the alert: "Cheaper" (competitor more expensive than me), "More expensive" (competitor cheaper than me), or "Price difference" (absolute difference between prices). Each option shows a different icon.',
		example: ES ? 'Mas barato | Mas caro | Diferencia de precio' : 'Cheaper | More expensive | Price difference',
		cleanup: drawerCleanup,
	},
	// ─── 12: Comparator Value ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-comp-value"]'),
		icon: 'input-numeric',
		title: ES ? 'Valor Umbral' : 'Threshold Value',
		description: ES
			? 'El valor numerico que define el umbral de la alerta. Dependiendo de la unidad seleccionada, puede ser un porcentaje (0-100%) o una cantidad en moneda. La alerta se dispara cuando la diferencia de precio supera este valor.'
			: 'The numeric value that defines the alert threshold. Depending on the selected unit, it can be a percentage (0-100%) or a currency amount. The alert fires when the price difference exceeds this value.',
		example: ES ? 'Ej: 10 → alerta si diferencia > 10% o > 10$' : 'E.g.: 10 → alert if difference > 10% or > $10',
		cleanup: drawerCleanup,
	},
	// ─── 13: Comparator Unit ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-comp-unit"]'),
		icon: 'percent',
		title: ES ? 'Unidad de Comparacion' : 'Comparison Unit',
		description: ES
			? 'La unidad del valor umbral: Porcentaje (%) o Moneda ($). El porcentaje es relativo al precio base; la moneda es una diferencia absoluta. Elige porcentaje para mercados con precios variables y moneda para limites fijos.'
			: 'The unit for the threshold value: Percentage (%) or Currency ($). Percentage is relative to the base price; currency is an absolute difference. Use percentage for variable-price markets and currency for fixed limits.',
		cleanup: drawerCleanup,
	},
	// ─── 14: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-sites"]'),
		icon: 'globe',
		title: ES ? 'Sitios Objetivo' : 'Target Sites',
		description: ES
			? 'Los sitios web de la competencia con los que quieres comparar. Sus precios se compararan contra el sitio principal. Puedes seleccionar multiples sitios — la alerta se activa si CUALQUIERA de ellos cumple la condicion.'
			: 'The competitor websites to compare against. Their prices will be compared to the main site. You can select multiple sites — the alert triggers if ANY of them meets the condition.',
		cleanup: drawerCleanup,
	},
	// ─── 15: Providers ───────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-providers"]'),
		icon: 'car-building',
		title: ES ? 'Proveedores Objetivo' : 'Target Providers',
		description: ES
			? 'Los proveedores de los sitios objetivo a monitorizar. El interruptor de exclusion permite cambiar entre "incluir solo estos proveedores" y "excluir estos proveedores". Puedes combinar filtro por tier y por proveedor individual.'
			: 'The providers from target sites to monitor. The exclusion toggle switches between "include only these providers" and "exclude these providers". You can combine tier filters with individual provider filters.',
		cleanup: drawerCleanup,
	},
	// ─── 16: Comparison Type (ACRISS/Category) ───────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-comp-type"]'),
		icon: 'car',
		title: ES ? 'Tipo de Vehiculo (ACRISS / Categoria)' : 'Vehicle Type (ACRISS / Category)',
		description: ES
			? 'Filtra por tipo de vehiculo para comparaciones mas precisas. Puedes elegir entre codigo ACRISS (especifico, ej: ECMR) o categoria (general, ej: Economy). Dejalo vacio para comparar todos los vehiculos.'
			: 'Filter by vehicle type for more precise comparisons. You can choose between ACRISS code (specific, e.g.: ECMR) or category (general, e.g.: Economy). Leave empty to compare all vehicles.',
		example: ES ? 'ACRISS: ECMR, CCMR | Categoria: Economy, Compact' : 'ACRISS: ECMR, CCMR | Category: Economy, Compact',
		cleanup: drawerCleanup,
	},
	// ─── 17: Same Insurance ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-same-ins"]'),
		icon: 'shield-check',
		title: ES ? 'Mismo Seguro' : 'Same Insurance',
		description: ES
			? 'Activa esta opcion para comparar solo vehiculos con el mismo tipo de seguro (full, partial, etc.). Garantiza que la comparacion de precios sea justa al comparar opciones con cobertura equivalente.'
			: 'Enable this option to compare only vehicles with the same insurance type (full, partial, etc.). Ensures the price comparison is fair by comparing options with equivalent coverage.',
		cleanup: drawerCleanup,
	},
	// ─── 18: More Features → transition to Not Available ─────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pc-more"]'),
		icon: 'sliders',
		title: ES ? 'Mas Filtros (Opcional)' : 'More Filters (Optional)',
		description: ES
			? 'Filtros opcionales adicionales: politica de combustible (full-full, pre-pay...), kilometraje (ilimitado, limitado), ubicacion en aeropuerto (en terminal, fuera) y tipo de transmision (manual, automatico). Aplica solo los que necesites para mayor precision.'
			: 'Additional optional filters: fuel policy (full-full, pre-pay...), mileage (unlimited, limited), airport location (in terminal, off), and transmission type (manual, automatic). Apply only the ones you need for greater precision.',
		action: () => openDrawer('Not Available'),
		waitFor: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-na-name"]'),
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// DRAWER 2 — Not Available
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 19: Condition Name ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-na-name"]'),
		icon: 'pen-line',
		title: ES ? 'Condicion: No Disponible' : 'Condition: Not Available',
		description: ES
			? 'Estamos ahora en el formulario de No Disponible. Este tipo de alerta detecta cuando un proveedor o categoria deja de tener disponibilidad en los sitios monitorizados. El nombre de la condicion identifica la regla en la tabla.'
			: 'We are now in the Not Available form. This alert type detects when a provider or category loses availability on monitored sites. The condition name identifies the rule in the table.',
		reverseAction: () => openDrawer('Price Comparison'),
		cleanup: drawerCleanup,
	},
	// ─── 20: Main Sites ──────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-na-main-site"]'),
		icon: 'globe',
		title: ES ? 'Sitios a Monitorizar' : 'Sites to Monitor',
		description: ES
			? 'Los sitios web donde quieres detectar la no disponibilidad. La alerta se activa cuando el proveedor/categoria seleccionado no aparece como disponible en ninguno de estos sitios durante una ejecucion.'
			: 'The websites where you want to detect unavailability. The alert triggers when the selected provider/category does not appear as available on any of these sites during an execution.',
		cleanup: drawerCleanup,
	},
	// ─── 21: Providers not available ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-na-providers"]'),
		icon: 'building-circle-xmark',
		title: ES ? 'Proveedores No Disponibles' : 'Providers Not Available',
		description: ES
			? 'Los proveedores cuya no disponibilidad quieres detectar. Puedes filtrar por tier o seleccionar proveedores individuales. La alerta se dispara cuando ESTOS proveedores no tienen resultados en los sitios monitorizados.'
			: 'The providers whose unavailability you want to detect. You can filter by tier or select individual providers. The alert fires when THESE providers have no results on the monitored sites.',
		cleanup: drawerCleanup,
	},
	// ─── 22: Comparison Type ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-na-comp-type"]'),
		icon: 'car',
		title: ES ? 'Tipo de Vehiculo' : 'Vehicle Type',
		description: ES
			? 'Filtra la no disponibilidad por tipo de vehiculo: ACRISS o categoria. Util para detectar la ausencia de un segmento especifico (ej: "no hay coches economicos disponibles de Europcar en este sitio").'
			: 'Filter unavailability by vehicle type: ACRISS or category. Useful for detecting the absence of a specific segment (e.g.: "no economy cars available from Europcar on this site").',
		cleanup: drawerCleanup,
	},
	// ─── 23: Notify Availability ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-na-notify"]'),
		icon: 'bell-ring',
		title: ES ? 'Notificar cuando Vuelva a Estar Disponible' : 'Notify When Available Again',
		description: ES
			? 'Opcion unica de este tipo: activa "Notify when available" para recibir tambien una alerta cuando el proveedor vuelve a tener disponibilidad. Puedes configurar si los sitios y proveedores de la notificacion positiva son los mismos o diferentes a los de la alerta de no disponibilidad.'
			: 'Unique to this type: enable "Notify when available" to also receive an alert when the provider becomes available again. You can configure whether the sites and providers for the positive notification are the same or different from the unavailability alert.',
		cleanup: drawerCleanup,
	},
	// ─── 24: More Features → transition to Price Change ──────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-na-more"]'),
		icon: 'sliders',
		title: ES ? 'Mas Filtros' : 'More Filters',
		description: ES
			? 'Los mismos filtros opcionales de vehiculo que en Comparacion de Precios: combustible, kilometraje, terminal y transmision. Aplica solo los necesarios para afinar la deteccion de no disponibilidad.'
			: 'The same optional vehicle filters as in Price Comparison: fuel policy, mileage, terminal, and transmission. Apply only the ones needed to refine unavailability detection.',
		action: () => openDrawer('Price Change'),
		waitFor: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-pch-name"]'),
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// DRAWER 3 — Price Change
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 25: Condition Name ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-name"]'),
		icon: 'pen-line',
		title: ES ? 'Condicion: Cambio de Precio' : 'Condition: Price Change',
		description: ES
			? 'Estamos ahora en el formulario de Cambio de Precio. Este tipo detecta cuando el precio de un vehiculo cambia entre ejecuciones consecutivas del informe, comparando el precio actual con el de la ejecucion anterior.'
			: 'We are now in the Price Change form. This type detects when a vehicle price changes between consecutive report executions, comparing the current price with the previous execution\'s price.',
		reverseAction: () => openDrawer('Not Available'),
		cleanup: drawerCleanup,
	},
	// ─── 26: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-sites"]'),
		icon: 'globe',
		title: ES ? 'Sitios a Monitorizar' : 'Sites to Monitor',
		description: ES
			? 'Los sitios web donde quieres detectar cambios de precio. Cualquier variacion en el precio de los vehiculos de estos sitios entre ejecuciones del informe puede activar la alerta.'
			: 'The websites where you want to detect price changes. Any variation in vehicle prices on these sites between report executions can trigger the alert.',
		cleanup: drawerCleanup,
	},
	// ─── 27: Providers ───────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-providers"]'),
		icon: 'building',
		title: ES ? 'Proveedores' : 'Providers',
		description: ES
			? 'Los proveedores cuyo cambio de precio quieres monitorizar. El interruptor de exclusion permite incluir o excluir proveedores especificos. Deja vacio para monitorizar todos los proveedores.'
			: 'The providers whose price changes you want to monitor. The exclusion toggle allows including or excluding specific providers. Leave empty to monitor all providers.',
		cleanup: drawerCleanup,
	},
	// ─── 28: Comparator (changes) ────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-comparator"]'),
		icon: 'arrow-trend-up',
		title: ES ? 'Direccion del Cambio' : 'Change Direction',
		description: ES
			? 'Define si la alerta se activa cuando el precio sube, baja, o en ambas direcciones. Las opciones disponibles son especificas para cambios temporales: "Increased" (subida), "Decreased" (bajada) o ambas.'
			: 'Defines whether the alert triggers when the price increases, decreases, or in both directions. The available options are specific to temporal changes: "Increased" (price went up), "Decreased" (price went down), or both.',
		example: ES ? 'Increased | Decreased | Both' : 'Increased | Decreased | Both',
		cleanup: drawerCleanup,
	},
	// ─── 29: Comparator Value ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-comp-value"]'),
		icon: 'input-numeric',
		title: ES ? 'Valor del Cambio' : 'Change Value',
		description: ES
			? 'El umbral minimo de cambio que activa la alerta. Por ejemplo, si pones 5 y la unidad es porcentaje, solo se notificaran cambios de precio mayores al 5%. Filtra el ruido de pequenas fluctuaciones.'
			: 'The minimum change threshold that triggers the alert. For example, setting 5 with percentage unit means only price changes greater than 5% will notify. Filters out noise from minor fluctuations.',
		cleanup: drawerCleanup,
	},
	// ─── 30: Comparator Unit ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-comp-unit"]'),
		icon: 'percent',
		title: ES ? 'Unidad del Cambio' : 'Change Unit',
		description: ES
			? 'Si el cambio de precio se mide en porcentaje (%) o en moneda ($). El porcentaje es mas util para precios variables; la moneda para detectar cambios de un importe fijo independientemente del precio base.'
			: 'Whether the price change is measured in percentage (%) or currency ($). Percentage is more useful for variable prices; currency for detecting changes of a fixed amount regardless of the base price.',
		cleanup: drawerCleanup,
	},
	// ─── 31: Comparison Type ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-comp-type"]'),
		icon: 'car',
		title: ES ? 'Tipo de Vehiculo' : 'Vehicle Type',
		description: ES
			? 'Filtra los cambios de precio por tipo de vehiculo. Puedes monitorizar cambios en un codigo ACRISS especifico o en una categoria completa. Dejalo vacio para detectar cualquier cambio de precio.'
			: 'Filter price changes by vehicle type. You can monitor changes in a specific ACRISS code or an entire category. Leave empty to detect any price change.',
		cleanup: drawerCleanup,
	},
	// ─── 32: More Features → transition to Min & Max ─────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-pch-more"]'),
		icon: 'sliders',
		title: ES ? 'Mas Filtros' : 'More Filters',
		description: ES
			? 'Filtros de vehiculo opcionales para Cambio de Precio: misma politica de combustible, mismo kilometraje, misma ubicacion en terminal y misma transmision. Usando estos filtros aseguras que comparas el mismo tipo de vehiculo entre ejecuciones.'
			: 'Optional vehicle filters for Price Change: same fuel policy, same mileage, same terminal location, and same transmission. Using these filters ensures you compare the same vehicle type across executions.',
		action: () => openDrawer('Min & Max Price'),
		waitFor: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-mm-name"]'),
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// DRAWER 4 — Min & Max Price
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 33: Condition Name ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-name"]'),
		icon: 'pen-line',
		title: ES ? 'Condicion: Min y Max' : 'Condition: Min & Max',
		description: ES
			? 'Estamos ahora en el formulario de Precio Minimo y Maximo. Este tipo define limites de precio absolutos — alerta cuando el precio de un vehiculo esta por encima o por debajo de un umbral definido por la posicion en el ranking de precios.'
			: 'We are now in the Min & Max Price form. This type defines absolute price limits — alerts when a vehicle price is above or below a threshold defined by its position in the price ranking.',
		reverseAction: () => openDrawer('Price Change'),
		cleanup: drawerCleanup,
	},
	// ─── 34: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-sites"]'),
		icon: 'globe',
		title: ES ? 'Sitios a Monitorizar' : 'Sites to Monitor',
		description: ES
			? 'Los sitios web donde se comprobaran los limites de precio. Los precios de estos sitios se ordenaran para determinar la posicion del proveedor principal en el ranking.'
			: 'The websites where price limits will be checked. Prices from these sites will be ranked to determine the main provider\'s position in the price ranking.',
		cleanup: drawerCleanup,
	},
	// ─── 35: Main Providers ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-main-prov"]'),
		icon: 'building',
		title: ES ? 'Proveedor Principal (Referencia)' : 'Main Provider (Reference)',
		description: ES
			? 'Tu proveedor de referencia — el que quieres monitorizar en el ranking de precios. La alerta se activa cuando este proveedor ocupa una posicion determinada (la que configures en "Position Price") en el ranking de precios de los sitios monitorizados.'
			: 'Your reference provider — the one you want to monitor in the price ranking. The alert triggers when this provider occupies a specific position (the one you configure in "Position Price") in the price ranking across monitored sites.',
		cleanup: drawerCleanup,
	},
	// ─── 36: Position Price ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-position"]'),
		icon: 'trophy',
		title: ES ? 'Posicion en el Ranking' : 'Position in Ranking',
		description: ES
			? 'La posicion del proveedor principal en el ranking de precios (1 = el mas barato, 2 = el segundo mas barato, etc.). La alerta se activa cuando el proveedor principal ocupa EXACTAMENTE esta posicion en el ranking de todos los sitios.'
			: 'The main provider\'s position in the price ranking (1 = cheapest, 2 = second cheapest, etc.). The alert triggers when the main provider occupies EXACTLY this position in the ranking across all sites.',
		example: ES ? 'Posicion 1 = el mas barato | Posicion 3 = tercero mas barato' : 'Position 1 = cheapest | Position 3 = third cheapest',
		cleanup: drawerCleanup,
	},
	// ─── 37: Comparator (min_max) ────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-comparator"]'),
		icon: 'greater-than',
		title: ES ? 'Condicion del Precio' : 'Price Condition',
		description: ES
			? 'Define si la alerta se activa cuando el precio del proveedor es "Higher than" (superior a) o "Lower than" (inferior a) el precio de referencia en la posicion configurada. Combina con el valor y la unidad para definir el umbral exacto.'
			: 'Defines whether the alert triggers when the provider price is "Higher than" or "Lower than" the reference price at the configured position. Combine with value and unit to define the exact threshold.',
		example: ES ? 'Higher than | Lower than' : 'Higher than | Lower than',
		cleanup: drawerCleanup,
	},
	// ─── 38: Comparator Diff ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-comp-diff"]'),
		icon: 'arrows-up-down',
		title: ES ? 'Referencia de Comparacion' : 'Comparison Reference',
		description: ES
			? 'Define con que precio se compara el precio del proveedor principal: el mas caro de los sitios, el mas barato, la media, u otros. Este es el precio "de referencia" contra el que se aplica la condicion Higher/Lower than.'
			: 'Defines which price the main provider price is compared against: the most expensive across sites, the cheapest, the average, or others. This is the "reference" price against which the Higher/Lower than condition is applied.',
		example: ES ? 'Mas caro | Mas barato | Media' : 'Most expensive | Cheapest | Average',
		cleanup: drawerCleanup,
	},
	// ─── 39: Comparator Value ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-comp-value"]'),
		icon: 'input-numeric',
		title: ES ? 'Valor del Limite' : 'Limit Value',
		description: ES
			? 'El valor numerico del limite de precio. Por ejemplo, si el comparador es "Higher than", la referencia es "el mas barato" y el valor es 20 con unidad porcentaje: la alerta salta si mi precio es mas de un 20% superior al mas barato.'
			: 'The numeric value of the price limit. For example, if comparator is "Higher than", reference is "cheapest", and value is 20 with percentage unit: the alert fires if my price is more than 20% above the cheapest.',
		cleanup: drawerCleanup,
	},
	// ─── 40: Comparator Unit ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-comp-unit"]'),
		icon: 'percent',
		title: ES ? 'Unidad del Limite' : 'Limit Unit',
		description: ES
			? 'Si el limite se mide en porcentaje (%) o en moneda ($). El porcentaje es relativo al precio de referencia; la moneda es una diferencia absoluta. Elige segun la precision que necesites.'
			: 'Whether the limit is measured in percentage (%) or currency ($). Percentage is relative to the reference price; currency is an absolute difference. Choose based on the precision you need.',
		cleanup: drawerCleanup,
	},
	// ─── 41: Providers ───────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-providers"]'),
		icon: 'building',
		title: ES ? 'Proveedores Objetivo' : 'Target Providers',
		description: ES
			? 'Los proveedores de los sitios objetivo que se incluyen en el ranking de precios. Puedes incluir o excluir proveedores para controlar que competidores entran en el calculo del ranking.'
			: 'The providers from target sites included in the price ranking. You can include or exclude providers to control which competitors enter the ranking calculation.',
		cleanup: drawerCleanup,
	},
	// ─── 42: Comparison Type ─────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-comp-type"]'),
		icon: 'car',
		title: ES ? 'Tipo de Vehiculo' : 'Vehicle Type',
		description: ES
			? 'Filtra el ranking de precios por tipo de vehiculo. ACRISS para un modelo especifico, Categoria para un segmento completo. Asegura que el ranking compara vehiculos equivalentes entre todos los sitios.'
			: 'Filter the price ranking by vehicle type. ACRISS for a specific model, Category for an entire segment. Ensures the ranking compares equivalent vehicles across all sites.',
		cleanup: drawerCleanup,
	},
	// ─── 43: Same Insurance ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-same-ins"]'),
		icon: 'shield-check',
		title: ES ? 'Mismo Seguro' : 'Same Insurance',
		description: ES
			? 'Activa para comparar solo vehiculos con la misma cobertura de seguro en el ranking. Evita comparaciones injustas entre un vehiculo con seguro completo y uno con cobertura basica.'
			: 'Enable to compare only vehicles with the same insurance coverage in the ranking. Avoids unfair comparisons between a vehicle with full insurance and one with basic coverage.',
		cleanup: drawerCleanup,
	},
	// ─── 44: More Features → transition to Rating ─────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-mm-more"]'),
		icon: 'sliders',
		title: ES ? 'Mas Filtros' : 'More Filters',
		description: ES
			? 'Los filtros adicionales de vehiculo para el ranking de precios: combustible, kilometraje, terminal y transmision. Cuantos mas filtros actives, mas especifica sera la comparacion — pero podria haber menos resultados disponibles.'
			: 'Additional vehicle filters for the price ranking: fuel policy, mileage, terminal, and transmission. The more filters you enable, the more specific the comparison — but there may be fewer available results.',
		action: () => openDrawer('Rating'),
		waitFor: () => isAlertsDrawerOpen.value && !!document.querySelector('[data-tour-id="cond-rt-name"]'),
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// DRAWER 5 — Rating
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 45: Condition Name ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-rt-name"]'),
		icon: 'pen-line',
		title: ES ? 'Condicion: Valoracion' : 'Condition: Rating',
		description: ES
			? 'Estamos ahora en el formulario de Valoracion — el mas sencillo. Monitoriza las puntuaciones de clientes de proveedores en sitios especificos y alerta cuando superen o caigan por debajo de un umbral.'
			: 'We are now in the Rating form — the simplest one. Monitor customer scores for providers on specific sites and alert when they exceed or fall below a threshold.',
		reverseAction: () => openDrawer('Min & Max Price'),
		cleanup: drawerCleanup,
	},
	// ─── 46: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-rt-sites"]'),
		icon: 'globe',
		title: ES ? 'Sitios a Monitorizar' : 'Sites to Monitor',
		description: ES
			? 'Los sitios web donde se comprobaran las valoraciones. Las valoraciones de clientes en estos sitios se compararan contra el umbral configurado. Solo sitios que publiquen valoraciones de proveedores son validos.'
			: 'The websites where ratings will be checked. Customer ratings on these sites will be compared against the configured threshold. Only sites that publish provider ratings are valid.',
		cleanup: drawerCleanup,
	},
	// ─── 47: Rating Providers ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-rt-providers"]'),
		icon: 'star',
		title: ES ? 'Proveedores a Monitorizar' : 'Providers to Monitor',
		description: ES
			? 'Los proveedores cuyas valoraciones quieres vigilar. Puedes incluir o excluir proveedores. La alerta se activa cuando la valoracion de UN proveedor supera o cae por debajo del umbral definido.'
			: 'The providers whose ratings you want to watch. You can include or exclude providers. The alert triggers when ONE provider\'s rating exceeds or falls below the defined threshold.',
		cleanup: drawerCleanup,
	},
	// ─── 48: Comparator (rating) ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-rt-comparator"]'),
		icon: 'scale-balanced',
		title: ES ? 'Condicion de Valoracion' : 'Rating Condition',
		description: ES
			? 'La condicion que activa la alerta: "Less than" (valoracion cae por debajo del umbral), "Greater than" (valoracion supera el umbral) o "Different than" (la valoracion cambia respecto al valor anterior, sin importar la direccion).'
			: 'The condition that triggers the alert: "Less than" (rating falls below threshold), "Greater than" (rating exceeds threshold), or "Different than" (rating changes from previous value, regardless of direction).',
		example: ES ? 'Less than | Greater than | Different than' : 'Less than | Greater than | Different than',
		cleanup: drawerCleanup,
	},
	// ─── 49: Comparator Value → close drawer ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="cond-rt-comp-value"]'),
		icon: 'input-numeric',
		title: ES ? 'Valor de Valoracion' : 'Rating Value',
		description: ES
			? 'El umbral de valoracion que activa la alerta (entre 1 y 10). Por ejemplo, si el comparador es "Less than" y el valor es 7: la alerta se activa cuando la valoracion del proveedor baja de 7. Guarda con "Save" cuando estes listo.'
			: 'The rating threshold that triggers the alert (between 1 and 10). For example, if comparator is "Less than" and value is 7: the alert fires when the provider\'s rating drops below 7. Save with "Save" when ready.',
		example: ES ? 'Ej: Less than 7 → alerta si valoracion < 7' : 'E.g.: Less than 7 → alert if rating < 7',
		action: () => clearSignals(),
		waitFor: () => !isAlertsDrawerOpen.value,
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// TABLE SECTION
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 50: Legend & Filters ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Percentage') || findByText('Currency') || findByText('Cheaper')
			if (!el) return null
			let parent = el.parentElement
			for (let i = 0; i < 5; i++) {
				if (!parent) break
				const text = parent.textContent.toLowerCase()
				if ((text.includes('percentage') || text.includes('currency')) &&
					(text.includes('cheaper') || text.includes('expensive') || text.includes('most'))) {
					if (parent.children.length >= 2) return parent
				}
				parent = parent.parentElement
			}
			return el.parentElement?.parentElement
		},
		skipIf: () => {
			const el = findByText('Percentage') || findByText('Currency')
			return !el
		},
		icon: 'filter',
		title: ES ? 'Leyenda y Filtros' : 'Legend & Filters',
		description: ES
			? 'Leyenda interactiva que funciona tambien como filtro. Haz clic en cualquier icono para filtrar la tabla por ese tipo de comparacion o unidad. Los iconos se animan al seleccionarlos. La leyenda varia segun la pestana de tipo de alerta activa.'
			: 'Interactive legend that doubles as a filter. Click any icon to filter the table by that comparison type or unit. Icons animate when selected. The legend varies depending on the active alert type tab.',
		example: ES
			? 'Clic en Porcentaje → muestra solo condiciones en % | Clic en Moneda → muestra solo condiciones en $'
			: 'Click Percentage → shows only % conditions | Click Currency → shows only $ conditions',
	},
	// ─── 51: Conditions Table ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			return document.querySelector('.MuiTable-root')?.closest('.MuiPaper-root') ||
				document.querySelector('[class*="MUIDataTable"]')
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de Condiciones' : 'Conditions Table',
		description: ES
			? 'Lista todas las condiciones del tipo de alerta activo. Cada fila muestra el nombre de la condicion, sitio principal, proveedores principales, regla de comparacion, sitios objetivo, proveedores objetivo y acciones de editar/eliminar.'
			: 'Lists all conditions for the active alert type. Each row shows the condition name, main site, main providers, comparison rule, target sites, target providers, and edit/delete actions.',
	},
	// ─── 52: Edit & Delete ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Actions'),
		icon: 'pen',
		title: ES ? 'Editar y Eliminar' : 'Edit & Delete',
		description: ES
			? 'Cada condicion se puede editar (icono de lapiz) o eliminar (icono de papelera). Al editar se abre el mismo formulario lateral prerrellenado con la configuracion actual de la condicion. La eliminacion requiere confirmacion.'
			: 'Each condition can be edited (pencil icon) or deleted (trash icon). Editing opens the same drawer form pre-filled with the condition\'s current configuration. Deletion requires confirmation.',
		example: ES
			? 'Lapiz = Editar condicion | Papelera = Eliminar condicion'
			: 'Pencil = Edit condition | Trash = Delete condition',
	},
	// ─── 53: Outro ───────────────────────────────────────────────────────────────
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Empieza seleccionando una pestana de tipo de alerta y luego crea tu primera condicion. Las condiciones se aplican a tus informes — cuando los datos de ejecucion coincidan con una condicion, recibiras una notificacion por email.'
			: 'Start by selecting an alert type tab, then create your first condition. Conditions are applied to your reports — when execution data matches a condition, you\'ll receive a notification via email.',
	},
]